import { SwapEventName } from '@uniswap/analytics-events'
import { TradeType } from '@uniswap/sdk-core'
import { providers } from 'ethers/lib/ethers'
import { useEffect, useMemo, useRef } from 'react'
import { WithV4Flag } from 'uniswap/src/data/apiClients/tradingApi/TradingApiClient'
import { useTradingApiSwapQuery } from 'uniswap/src/data/apiClients/tradingApi/useTradingApiSwapQuery'
import { getTradeSettingsDeadline } from 'uniswap/src/data/apiClients/tradingApi/utils/getTradeSettingsDeadline'
import {
  CreateSwapRequest,
  NullablePermit,
  Routing,
  TransactionFailureReason,
} from 'uniswap/src/data/tradingApi/__generated__/index'
import { AccountMeta } from 'uniswap/src/features/accounts/types'
import {
  convertGasFeeToDisplayValue,
  useActiveGasStrategy,
  useShadowGasStrategies,
  useTransactionGasFee,
} from 'uniswap/src/features/gas/hooks'
import { GasFeeResult, areEqualGasStrategies } from 'uniswap/src/features/gas/types'
import { DynamicConfigs, SwapConfigKey } from 'uniswap/src/features/gating/configs'
import { FeatureFlags } from 'uniswap/src/features/gating/flags'
import { useDynamicConfigValue, useFeatureFlag } from 'uniswap/src/features/gating/hooks'
import { useLocalizationContext } from 'uniswap/src/features/language/LocalizationContext'
import { sendAnalyticsEvent } from 'uniswap/src/features/telemetry/send'
import { useTransactionSettingsContext } from 'uniswap/src/features/transactions/settings/contexts/TransactionSettingsContext'
import { getBaseTradeAnalyticsPropertiesFromSwapInfo } from 'uniswap/src/features/transactions/swap/analytics'
import { buildPermitFromSignature } from 'uniswap/src/features/transactions/swap/hooks/usePermit2Signature'
import { useSwapFlowState } from 'uniswap/src/features/transactions/swap/hooks/useSwapFlowState'
import { useWrapTransactionRequest } from 'uniswap/src/features/transactions/swap/hooks/useWrapTransactionRequest'
import { DerivedSwapInfo } from 'uniswap/src/features/transactions/swap/types/derivedSwapInfo'
import { SwapGasFeeEstimation } from 'uniswap/src/features/transactions/swap/types/swapTxAndGasInfo'
import { ApprovalAction, TokenApprovalInfo } from 'uniswap/src/features/transactions/swap/types/trade'
import { isUniswapX } from 'uniswap/src/features/transactions/swap/utils/routing'
import {
  SWAP_GAS_URGENCY_OVERRIDE,
  getBridgeQuoteFromResponse,
  getClassicQuoteFromResponse,
  isClassicQuote,
} from 'uniswap/src/features/transactions/swap/utils/tradingApi'
import { GasFeeEstimates } from 'uniswap/src/features/transactions/types/transactionDetails'
import { WrapType } from 'uniswap/src/features/transactions/types/wrap'
import { isDetoxBuild } from 'utilities/src/environment/constants'
import { logger } from 'utilities/src/logger/logger'
import { isInterface, isMobileApp } from 'utilities/src/platform'
import { useTrace } from 'utilities/src/telemetry/trace/TraceContext'
import { ONE_SECOND_MS } from 'utilities/src/time/time'

export const UNKNOWN_SIM_ERROR = 'Unknown gas simulation error'

// TODO(UniswapX): add fallback gas limits per chain? l2s have higher costs
export const WRAP_FALLBACK_GAS_LIMIT_IN_GWEI = 45_000

const FALLBACK_SWAP_REQUEST_POLL_INTERVAL_MS = 1000
export interface TransactionRequestInfo {
  transactionRequest: providers.TransactionRequest | undefined
  permitSignature: string | undefined
  permitData?: NullablePermit
  permitDataLoading?: boolean
  gasFeeResult: GasFeeResult
  gasEstimate: SwapGasFeeEstimation
  swapRequestArgs: CreateSwapRequest | undefined
  simulationFailureReasons?: TransactionFailureReason[]
}

export function useTransactionRequestInfo({
  derivedSwapInfo,
  tokenApprovalInfo,
  account,
  skip,
}: {
  derivedSwapInfo: DerivedSwapInfo
  tokenApprovalInfo: TokenApprovalInfo | undefined
  account?: AccountMeta
  skip: boolean
}): TransactionRequestInfo {
  const formatter = useLocalizationContext()
  const trace = useTrace()
  const activeGasStrategy = useActiveGasStrategy(derivedSwapInfo.chainId, 'general')
  const shadowGasStrategies = useShadowGasStrategies(derivedSwapInfo.chainId, 'general')
  const v4Enabled = useFeatureFlag(FeatureFlags.V4Swap)
  const transactionSettings = useTransactionSettingsContext()

  const { trade: tradeWithStatus } = derivedSwapInfo
  const { trade } = tradeWithStatus || { trade: undefined }

  const isBridgeTrade = trade?.routing === Routing.BRIDGE
  const permitData = trade?.quote?.permitData
  // checks within functions for type of trade
  const swapQuote = getClassicQuoteFromResponse(trade?.quote) ?? getBridgeQuoteFromResponse(trade?.quote)

  // Quote indicates we need to include a signed permit message
  const requiresPermit2Sig = !!permitData
 
  // 禁用自动签名：不再在 hook 阶段触发 Permit2 签名，统一交由 saga 步骤处理
  const signatureInfo = {
    signature: undefined as string | undefined,
    isLoading: false,
  }


  /**
   * Simulate transactions to ensure they will not fail on-chain.
   * Do not simulate for bridge transactions or txs that need an approval
   * as those require Tenderly to simulate and it is not currently integrated into the gas servic
   */
  const shouldSimulateTxn = isBridgeTrade ? false : tokenApprovalInfo?.action === ApprovalAction.None
  const missingSig = requiresPermit2Sig && !signatureInfo.signature

  // Format request args
  const swapRequestArgs: WithV4Flag<CreateSwapRequest> | undefined = useMemo(() => {
    // TODO: MOB(2438) https://linear.app/uniswap/issue/MOB-2438/uniswap-x-clean-old-trading-api-code
    if (!swapQuote) {
      return undefined
    }

    const deadline = getTradeSettingsDeadline(transactionSettings.customDeadline)

    const permit = signatureInfo.signature && permitData 
      ? buildPermitFromSignature(signatureInfo.signature, permitData) ?? undefined
      : undefined

    const swapArgs: WithV4Flag<CreateSwapRequest> = {
      quote: swapQuote,
      permitData: permitData ?? undefined,
      signature: signatureInfo.signature,
      permit,
      simulateTransaction: shouldSimulateTxn,
      deadline,
      refreshGasPrice: true,
      gasStrategies: [activeGasStrategy, ...(shadowGasStrategies ?? [])],
      urgency: SWAP_GAS_URGENCY_OVERRIDE,
      v4Enabled,
      // 🔧 添加原始 currency 信息，用于正确处理原生代币
      _currencyInIsNative: trade?.inputAmount?.currency.isNative,
      _currencyOutIsNative: trade?.outputAmount?.currency.isNative,
    }


    return swapArgs
  }, [
    activeGasStrategy,
    transactionSettings.customDeadline,
    permitData,
    shadowGasStrategies,
    shouldSimulateTxn,
    signatureInfo.signature,
    swapQuote,
    trade?.inputAmount?.quotient,
    v4Enabled,
  ])

  // Wrap transaction request
  const isUniswapXWrap = trade && isUniswapX(trade) && trade.needsWrap
  const isWrapApplicable = derivedSwapInfo.wrapType !== WrapType.NotApplicable || isUniswapXWrap
  const wrapTxRequest = useWrapTransactionRequest(derivedSwapInfo, account)
  const currentWrapGasFee = useTransactionGasFee(
    wrapTxRequest,
    !isWrapApplicable,
    undefined,
    WRAP_FALLBACK_GAS_LIMIT_IN_GWEI * 10e9,
  ) // Skip Gas Fee API call on transactions that don't need wrapping

  const wrapGasFeeRef = useRef(currentWrapGasFee)
  if (currentWrapGasFee.value) {
    wrapGasFeeRef.current = currentWrapGasFee
  }

  // Wrap gas cost should not change significantly between trades, so we can use the last value if current is unavailable.
  const wrapGasFee: GasFeeResult = useMemo(
    () => ({
      ...currentWrapGasFee,
      value: currentWrapGasFee.value ?? wrapGasFeeRef.current.value,
      displayValue: currentWrapGasFee.displayValue ?? wrapGasFeeRef.current.displayValue,
    }),
    [currentWrapGasFee],
  )

  const wrapTxRequestWithGasFee = useMemo(
    () => ({ ...wrapTxRequest, ...(wrapGasFee.params ?? {}) }),
    [wrapTxRequest, wrapGasFee],
  )

  // 🔧 TIMING FIX: Don't skip transaction request for permit2 flow even without signature yet
  const skipTransactionRequest = !swapRequestArgs || isWrapApplicable || skip || (missingSig && !isInterface)

  const tradingApiSwapRequestMs = useDynamicConfigValue(
    DynamicConfigs.Swap,
    SwapConfigKey.TradingApiSwapRequestMs,
    FALLBACK_SWAP_REQUEST_POLL_INTERVAL_MS,
  )

  const {
    data,
    error,
    isLoading: isSwapLoading,
  } = useTradingApiSwapQuery({
    params: skipTransactionRequest ? undefined : swapRequestArgs,
    // Disable polling during e2e testing because it was preventing js thread from going idle
    refetchInterval: isDetoxBuild ? undefined : tradingApiSwapRequestMs,
    staleTime: tradingApiSwapRequestMs,
    // We add a small buffer in case connection is too slow
    immediateGcTime: tradingApiSwapRequestMs + ONE_SECOND_MS * 5,
  })

  // We use the gasFee estimate from swap response, as its more accurate
  const swapGasFee = useMemo(
    () => ({
      value: data?.gasFee,
      displayValue: convertGasFeeToDisplayValue(data?.gasFee, activeGasStrategy),
    }),
    [data?.gasFee, activeGasStrategy],
  )

  // This is a case where simulation fails on backend, meaning txn is expected to fail
  const simulationFailureReasons = isClassicQuote(swapQuote) ? swapQuote?.txFailureReasons : undefined
  const simulationError =
    simulationFailureReasons?.includes(TransactionFailureReason.SIMULATION_ERROR) ||
    simulationFailureReasons?.includes(TransactionFailureReason.SLIPPAGE_TOO_LOW)

  const gasEstimateError = useMemo(
    () => (simulationError ? new Error(UNKNOWN_SIM_ERROR) : error),
    [simulationError, error],
  )

  const gasFeeResult = useMemo(
    () => ({
      value: isWrapApplicable ? wrapGasFee.value : swapGasFee.value,
      displayValue: isWrapApplicable ? wrapGasFee.displayValue : swapGasFee.displayValue,
      isLoading: isWrapApplicable ? wrapGasFee.isLoading : isSwapLoading,
      error: isWrapApplicable ? wrapGasFee.error : gasEstimateError,
    }),
    [isWrapApplicable, wrapGasFee, swapGasFee, isSwapLoading, gasEstimateError],
  )

  // Only log analytics events once per request
  const previousRequestIdRef = useRef(trade?.quote?.requestId)

  useEffect(() => {
    // Only log errors if we have a valid quote with requestId
    if (!trade?.quote || !trade.quote.requestId) {
      return
    }

    const currentRequestId = trade.quote.requestId
    const isNewQuote = previousRequestIdRef.current !== currentRequestId

    // reset request id
    previousRequestIdRef.current = currentRequestId

    if (!isNewQuote) {
      return
    }

    if (gasEstimateError) {
      logger.warn('useTransactionRequestInfo', 'useTransactionRequestInfo', UNKNOWN_SIM_ERROR, {
        ...getBaseTradeAnalyticsPropertiesFromSwapInfo({ derivedSwapInfo, transactionSettings, formatter, trace }),
        error: gasEstimateError,
        simulationFailureReasons: isClassicQuote(swapQuote) ? swapQuote?.txFailureReasons : undefined,
        txRequest: data?.calls?.[0] || data?.swap,
      })

      if (!isMobileApp) {
        sendAnalyticsEvent(SwapEventName.SWAP_ESTIMATE_GAS_CALL_FAILED, {
          ...getBaseTradeAnalyticsPropertiesFromSwapInfo({ derivedSwapInfo, transactionSettings, formatter, trace }),
          error: gasEstimateError,
          txRequest: data?.calls?.[0] || data?.swap,
          simulationFailureReasons: isClassicQuote(swapQuote) ? swapQuote?.txFailureReasons : undefined,
        })
      }
    }
  }, [
    data,
    transactionSettings,
    derivedSwapInfo,
    formatter,
    gasEstimateError,
    swapRequestArgs,
    trade,
    trace,
    swapQuote,
  ])

  const gasEstimate: SwapGasFeeEstimation = useMemo(() => {
    const activeGasEstimate = data?.gasEstimates?.find((e) => areEqualGasStrategies(e.strategy, activeGasStrategy))
    const swapGasEstimate: GasFeeEstimates | undefined = activeGasEstimate
      ? {
          activeEstimate: activeGasEstimate,
          shadowEstimates: data?.gasEstimates?.filter((e) => e !== activeGasEstimate),
        }
      : undefined
    return {
      swapEstimates: swapGasEstimate,
      wrapEstimates: wrapGasFee.gasEstimates,
    }
  }, [data?.gasEstimates, activeGasStrategy, wrapGasFee.gasEstimates])

  // 🔧 FIX: Ensure swap transaction request has chainId for validation and resolve Gas parameter conflicts
  const swapTxRequest = useMemo(() => {
    if (!data) return undefined
    
    // 🚨 CRITICAL FIX: 处理新的 API 响应格式 - 检查 calls[0] 或 swap 字段
    let transactionData: any = null
    
    // 🔧 类型安全的 API 响应处理
    const apiResponse = data as any // 临时类型断言，因为新的 API 格式还没有正式类型定义
    
    if (apiResponse.calls && Array.isArray(apiResponse.calls) && apiResponse.calls.length > 0) {
      // 新格式：使用 calls[0]
      const firstCall = apiResponse.calls[0]
      if (!firstCall?.to) {
        console.error('❌ useTransactionRequestInfo: API 响应的 calls[0] 缺少 to 字段', {
          calls: apiResponse.calls,
          firstCall,
          hasData: !!data,
          dataKeys: data ? Object.keys(data) : null,
        })
        return undefined
      }
      
      transactionData = {
        to: firstCall.to,
        data: firstCall.data,
        value: firstCall.value || '0x00',
        chainId: apiResponse.chainId,
        from: apiResponse.from,
        gasLimit: apiResponse.gasLimit,
        gasPrice: apiResponse.gasPrice,
        // 🔧 处理可能的 EIP-1559 字段
        maxFeePerGas: apiResponse.maxFeePerGas,
        maxPriorityFeePerGas: apiResponse.maxPriorityFeePerGas,
      }
      
    } else if (data.swap) {
      // 旧格式：使用 swap 字段 (向后兼容)
      if (!data.swap.to) {
        console.error('❌ useTransactionRequestInfo: API 响应的 swap 字段缺少 to 字段', {
          swapData: data.swap,
          swapKeys: Object.keys(data.swap),
          hasData: !!data,
          dataKeys: data ? Object.keys(data) : null,
        })
        return undefined
      }
      
      transactionData = data.swap
    } else {
      console.error('❌ useTransactionRequestInfo: API 响应既没有 calls 也没有 swap 字段', {
        hasData: !!data,
        dataKeys: data ? Object.keys(data) : null,
      })
      return undefined
    }
    
    // Add chainId and from if missing from API response
    const baseRequest = {
      ...transactionData,
      chainId: transactionData.chainId || derivedSwapInfo.chainId, // Use derivedSwapInfo.chainId as fallback
      from: transactionData.from || account?.address, // Add from address if missing
    }
    
    // 🚨 CRITICAL FIX: Resolve Gas parameter conflicts
    // MetaMask doesn't allow mixing gasPrice (Legacy) with maxFeePerGas (EIP-1559)
    // Priority: Use EIP-1559 if both maxFeePerGas and maxPriorityFeePerGas are present
    if (baseRequest.maxFeePerGas && baseRequest.maxPriorityFeePerGas) {
      // Use EIP-1559 format, remove legacy gasPrice
      const { gasPrice, ...eip1559Request } = baseRequest
      return eip1559Request
    } else if (baseRequest.gasPrice) {
      // Use Legacy format, remove EIP-1559 parameters
      const { maxFeePerGas, maxPriorityFeePerGas, ...legacyRequest } = baseRequest
      return legacyRequest
    }
    
    // If no gas parameters are present, return as-is (this shouldn't happen)
    return baseRequest
  }, [data, account?.address, derivedSwapInfo.chainId])

  return {
    gasFeeResult,
    transactionRequest: isWrapApplicable ? wrapTxRequestWithGasFee : swapTxRequest,
    permitSignature: signatureInfo.signature,
    permitDataLoading: signatureInfo.isLoading,
    permitData,
    gasEstimate,
    swapRequestArgs,
    simulationFailureReasons,
  }
}
